import { useState, useEffect } from "react";
import {
  HelpCircle,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Gauge,
  TrendingUp,
  Hourglass,
  Lock,
  CreditCard,
  ShieldCheck,
  Database,
  LucideIcon,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { logout } from "@/services/userLogin";
import affiniaLogo from "@/assets/affinia-logo.png";

const metrics: { title: string; slug: string; icon: LucideIcon }[] = [
  { title: "Utilisation", slug: "utilisation-rate", icon: Gauge },
  { title: "Recovery", slug: "recovery-rate", icon: TrendingUp },
  { title: "WIP", slug: "wip-aged", icon: Hourglass },
  { title: "Debtors", slug: "debt-aged", icon: CreditCard },
  { title: "Lockup", slug: "lockup", icon: Lock },
];

const metricPathAliases: Record<string, string[]> = {
  "debt-aged": ["/debtors-current/drill-through"],
};

function isMetricRouteActive(slug: string, currentPath: string): boolean {
  return currentPath.includes(`/metrics/${slug}`) || (metricPathAliases[slug] ?? []).some((path) => currentPath.startsWith(path));
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { state, toggleSidebar, isMobile, setOpenMobile } = useSidebar();
  // On mobile the sidebar renders inside a full-width Sheet, so it always shows
  // its expanded content there — the collapsed rail only exists from `md` up.
  const isCollapsed = state === "collapsed" && !isMobile;

  /** Tapping a link on mobile navigates *behind* the drawer, which then has to
   *  be dismissed by hand. Close it as part of the navigation instead. */
  const closeMobileNav = () => {
    if (isMobile) setOpenMobile(false);
  };

  const [userName, setUserName] = useState<string>("");
  const [userRole, setUserRole] = useState<string>("");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserName(user.name);
      setUserRole(user.role);
    }
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Sidebar collapsible="icon" className="border-r bg-sidebar-background">
      <SidebarContent
        className="bg-sidebar-background overflow-x-hidden overflow-y-auto"
        onClick={(event) => {
          // One handler rather than an onClick per NavLink: every navigation in
          // here is an anchor, and the drawer should close for all of them.
          if ((event.target as HTMLElement).closest("a")) closeMobileNav();
        }}
      >
        {/* Logo + collapse toggle. Fixed height so this header's bottom border
            aligns with the page header's one on the right (Admin, Datacore
            etc. render a py-3 title block that comes out ~61px tall). */}
        <div className={`h-[61px] border-b border-sidebar-border flex items-center ${isCollapsed ? "justify-center px-3" : "justify-between px-4"}`}>
          {!isCollapsed && (
            <img src={affiniaLogo} alt="Affinia" className="h-8 flex-1 min-w-0 object-contain object-left dark:brightness-0 dark:invert" />
          )}
          {!isMobile && (
            <button
              onClick={toggleSidebar}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="p-1.5 rounded-md hover:bg-sidebar-accent/50 text-sidebar-foreground transition-colors shrink-0"
            >
              {isCollapsed
                ? <PanelLeftOpen className="h-4 w-4" />
                : <PanelLeftClose className="h-4 w-4" />
              }
            </button>
          )}
        </div>

        <SidebarGroup className={isCollapsed ? "px-2 py-4" : "px-3 py-4"}>
          <SidebarMenu className="gap-1">
            {metrics.map((metric) => {
              const Icon = metric.icon;
              const isActive = isMetricRouteActive(metric.slug, currentPath);
              return (
                <SidebarMenuItem key={metric.slug}>
                  <SidebarMenuButton asChild tooltip={metric.title}>
                    <NavLink
                      to={`/metrics/${metric.slug}`}
                      activeClassName="bg-sidebar-primary text-sidebar-primary-foreground font-medium"
                      className={`hover:bg-sidebar-accent/50 text-sidebar-foreground ${isActive ? "bg-sidebar-primary text-sidebar-primary-foreground font-medium" : ""}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{metric.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}

            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="FAQ">
                <NavLink
                  to="/faq"
                  activeClassName="bg-sidebar-primary text-sidebar-primary-foreground font-medium"
                  className="hover:bg-sidebar-accent/50 text-sidebar-foreground"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>FAQ</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className={`bg-sidebar-background border-t border-sidebar-border ${isCollapsed ? "p-2" : "p-4"}`}>
        <SidebarMenu>
          {userRole === "admin" && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Admin">
                {/* `end` so /admin stops matching its descendants — without it
                    this stays highlighted on /admin/integrations/xero. */}
                <NavLink
                  to="/admin"
                  end
                  activeClassName="bg-sidebar-primary text-sidebar-primary-foreground font-medium"
                  className="hover:bg-sidebar-accent/50 text-sidebar-foreground"
                >
                  <ShieldCheck className="h-4 w-4" />
                  <span>Admin</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {userRole === "admin" && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Datacore">
                <NavLink
                  to="/datacore"
                  activeClassName="bg-sidebar-primary text-sidebar-primary-foreground font-medium"
                  className="hover:bg-sidebar-accent/50 text-sidebar-foreground"
                >
                  <Database className="h-4 w-4" />
                  <span>Datacore</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            {isCollapsed ? (
              <ThemeToggle variant="icon" />
            ) : (
              <ThemeToggle variant="menu" />
            )}
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              tooltip="Log Out"
              className="hover:bg-sidebar-accent/50 text-sidebar-foreground"
            >
              <LogOut className="h-4 w-4" />
              <span>Log Out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {!isCollapsed && (
          <button
            type="button"
            onClick={() => {
              closeMobileNav();
              navigate("/profile");
            }}
            title="View profile"
            className="flex w-full items-center gap-3 mt-2 px-2 py-3 rounded-lg text-left transition-colors hover:bg-sidebar-accent/50"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src="" />
              <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-semibold">{getInitials(userName)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-foreground truncate">{userName}</p>
              <p className="text-xs text-muted-foreground truncate">{userRole}</p>
            </div>
          </button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
